import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft, Activity, CheckCircle2, Circle, Save, Loader2,
  Building2, Globe, Phone, Mail, MapPin, FileText, Shield,
  Users, DollarSign, Stethoscope, Briefcase, ChevronDown,
  ChevronRight, Image, Hash, Award, BookOpen, Laptop, Megaphone,
  CreditCard, Lock, CheckSquare, AlertCircle, ExternalLink,
  Plus, X, Eye, EyeOff, KeyRound, Download, FileSpreadsheet, Printer,
  Images
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import DMEMediaPanel from '../components/DMEMediaPanel';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── DME STARTUP SECTIONS ──────────────────────────────────────
const DME_SECTIONS = [
  {
    id: 'business_identity',
    label: 'Business Identity',
    icon: Building2,
    color: 'from-blue-500 to-blue-700',
    description: 'Core business information visible to your client',
    type: 'fields',
    fields: [
      { id: 'company_name',    label: 'Legal Company Name',     type: 'text',  placeholder: 'e.g. ABC DME Solutions LLC' },
      { id: 'dba_name',        label: 'DBA / Trade Name',       type: 'text',  placeholder: 'Doing Business As...' },
      { id: 'domain_name',     label: 'Domain Name',            type: 'text',  placeholder: 'e.g. abcdme.com', icon: Globe },
      { id: 'website_url',     label: 'Website URL',            type: 'url',   placeholder: 'https://...', icon: Globe },
      { id: 'phone',           label: 'Primary Phone',          type: 'tel',   placeholder: '(555) 000-0000', icon: Phone },
      { id: 'fax',             label: 'Fax Number',             type: 'tel',   placeholder: '(555) 000-0000' },
      { id: 'email',           label: 'Business Email',         type: 'email', placeholder: 'contact@company.com', icon: Mail },
      { id: 'physical_address',label: 'Physical Address',       type: 'textarea', placeholder: '123 Main St, City, State ZIP', icon: MapPin },
      { id: 'mailing_address', label: 'Mailing Address',        type: 'textarea', placeholder: 'If different from physical' },
    ]
  },
  {
    id: 'online_presence',
    label: 'Online Presence & Branding',
    icon: Laptop,
    color: 'from-violet-500 to-purple-700',
    description: 'Website, logo, and digital identity',
    type: 'tasks',
    tasks: [
      { id: 'domain_registered',    label: 'Domain name registered' },
      { id: 'hosting_setup',        label: 'Web hosting account set up' },
      { id: 'logo_designed',        label: 'Company logo designed' },
      { id: 'website_live',         label: 'Website live and published' },
      { id: 'google_business',      label: 'Google Business Profile created' },
      { id: 'social_media',         label: 'Social media profiles set up (Facebook, LinkedIn)' },
      { id: 'email_setup',          label: 'Professional email addresses configured' },
      { id: 'ssl_certificate',      label: 'SSL certificate installed (HTTPS)' },
    ]
  },
  {
    id: 'business_formation',
    label: 'Business Formation',
    icon: FileText,
    color: 'from-slate-600 to-slate-800',
    description: 'Legal structure and foundational business setup',
    type: 'tasks',
    tasks: [
      { id: 'biz_structure',        label: 'Choose business structure (LLC, S-Corp, C-Corp)' },
      { id: 'state_registration',   label: 'Register business with state Secretary of State' },
      { id: 'ein_obtained',         label: 'Obtain EIN from IRS (Form SS-4)' },
      { id: 'bank_account',         label: 'Open business checking account' },
      { id: 'biz_license',          label: 'Obtain local business license/permit' },
      { id: 'operating_agreement',  label: 'Draft and execute operating agreement' },
      { id: 'biz_insurance',        label: 'Obtain general liability insurance' },
      { id: 'workers_comp',         label: 'Set up workers compensation insurance' },
    ]
  },
  {
    id: 'medicare_enrollment',
    label: 'Medicare / Medicaid Enrollment',
    icon: Shield,
    color: 'from-blue-600 to-cyan-700',
    description: 'CMS enrollment and supplier credentialing',
    type: 'mixed',
    fields: [
      { id: 'npi_number',         label: 'NPI Number (Type 2)',   type: 'text', placeholder: '10-digit NPI' },
      { id: 'ptan_number',        label: 'PTAN / Supplier Number',type: 'text', placeholder: 'Provider Transaction Access Number' },
      { id: 'surety_bond_number', label: 'Surety Bond Number',    type: 'text', placeholder: 'Bond reference number' },
      { id: 'surety_bond_amount', label: 'Surety Bond Amount',    type: 'text', placeholder: '$50,000' },
    ],
    tasks: [
      { id: 'npi_applied',          label: 'Applied for NPI (National Provider Identifier)' },
      { id: 'cms855s_submitted',    label: 'CMS-855S application submitted to Medicare' },
      { id: 'surety_bond_obtained', label: 'Medicare surety bond obtained ($50,000 minimum)' },
      { id: 'medicaid_enrolled',    label: 'State Medicaid enrollment completed' },
      { id: 'pecos_setup',          label: 'PECOS (Provider Enrollment) account set up' },
      { id: 'commercial_credentialing', label: 'Commercial insurance credentialing begun' },
      { id: 'medicare_approved',    label: 'Medicare supplier number approved' },
    ]
  },
  {
    id: 'accreditation',
    label: 'Accreditation & Compliance',
    icon: Award,
    color: 'from-emerald-600 to-green-700',
    description: 'Required accreditation and compliance program',
    type: 'mixed',
    fields: [
      { id: 'accreditation_body', label: 'Accreditation Body Selected', type: 'text', placeholder: 'e.g. ACHC, HQAA, CHAP, Joint Commission' },
      { id: 'accreditation_status', label: 'Accreditation Status', type: 'text', placeholder: 'e.g. Application submitted, Site visit scheduled' },
    ],
    tasks: [
      { id: 'accreditation_chosen',   label: 'Accreditation body selected (ACHC/HQAA/CHAP)' },
      { id: 'accreditation_applied',  label: 'Accreditation application submitted' },
      { id: 'pp_manual',              label: 'Policies & Procedures (P&P) manual created' },
      { id: 'hipaa_policies',         label: 'HIPAA privacy and security policies implemented' },
      { id: 'hipaa_training',         label: 'HIPAA staff training completed' },
      { id: 'compliance_officer',     label: 'Compliance officer designated' },
      { id: 'site_visit_passed',      label: 'Accreditation site visit passed' },
      { id: 'oig_exclusion_check',    label: 'OIG exclusion list checks in place' },
    ]
  },
  {
    id: 'physical_operations',
    label: 'Physical Location & Operations',
    icon: MapPin,
    color: 'from-amber-500 to-orange-600',
    description: 'Facility, equipment, and delivery setup',
    type: 'mixed',
    fields: [
      { id: 'facility_address',  label: 'Facility Address',      type: 'textarea', placeholder: 'Full facility address' },
      { id: 'facility_sqft',     label: 'Facility Size (sq ft)', type: 'text', placeholder: 'e.g. 1,200 sq ft' },
      { id: 'lease_expiration',  label: 'Lease Expiration Date', type: 'text', placeholder: 'MM/YYYY' },
    ],
    tasks: [
      { id: 'facility_secured',      label: 'Physical facility secured (lease/purchase)' },
      { id: 'facility_signage',      label: 'Required exterior signage installed' },
      { id: 'storage_setup',         label: 'Equipment storage and inventory area set up' },
      { id: 'delivery_vehicle',      label: 'Delivery vehicle acquired and insured' },
      { id: 'equipment_maintenance', label: 'Equipment cleaning and maintenance log created' },
      { id: 'service_area',          label: 'Service area defined and mapped' },
      { id: 'after_hours_plan',      label: 'After-hours emergency contact plan established' },
    ]
  },
  {
    id: 'billing_setup',
    label: 'Billing & Revenue Cycle',
    icon: CreditCard,
    color: 'from-green-600 to-teal-700',
    description: 'Billing software, EDI, and claims setup',
    type: 'mixed',
    fields: [
      { id: 'billing_software',   label: 'Billing Software',         type: 'text', placeholder: 'e.g. Brightree, Kareo, CPR+' },
      { id: 'clearinghouse',      label: 'Clearinghouse',            type: 'text', placeholder: 'e.g. Availity, Change Healthcare' },
      { id: 'tax_id',             label: 'Tax ID / EIN',             type: 'text', placeholder: 'XX-XXXXXXX' },
    ],
    tasks: [
      { id: 'billing_software_selected', label: 'Billing/DME software selected and contracted' },
      { id: 'edi_enrollment',      label: 'EDI enrollment with Medicare completed' },
      { id: 'clearinghouse_setup', label: 'Clearinghouse account set up' },
      { id: 'fee_schedule',        label: 'Medicare fee schedule reviewed' },
      { id: 'lcd_review',          label: 'LCD/LCA (Local Coverage Determinations) reviewed for products' },
      { id: 'cpt_hcpcs_training',  label: 'Staff trained on HCPCS/CPT coding' },
      { id: 'claim_workflow',      label: 'Claim submission workflow established' },
      { id: 'ar_process',          label: 'Accounts receivable follow-up process created' },
    ]
  },
  {
    id: 'product_lines',
    label: 'Product Lines & Equipment',
    icon: Stethoscope,
    color: 'from-red-500 to-rose-700',
    description: 'DME categories and supplier relationships',
    type: 'mixed',
    fields: [
      { id: 'primary_products',   label: 'Primary Product Lines',   type: 'textarea', placeholder: 'e.g. CPAP/BiPAP, wheelchairs, hospital beds, oxygen...' },
      { id: 'suppliers',          label: 'Key Suppliers / Vendors', type: 'textarea', placeholder: 'Supplier names and contacts' },
    ],
    tasks: [
      { id: 'product_lines_defined',  label: 'Product lines and equipment categories defined' },
      { id: 'supplier_agreements',    label: 'Supplier/vendor agreements signed' },
      { id: 'initial_inventory',      label: 'Initial inventory ordered and received' },
      { id: 'equipment_serial_log',   label: 'Equipment serial number tracking system set up' },
      { id: 'rental_agreements',      label: 'Rental agreement templates created' },
      { id: 'delivery_intake_forms',  label: 'Patient intake and delivery confirmation forms created' },
    ]
  },
  {
    id: 'staffing',
    label: 'Staffing & Training',
    icon: Users,
    color: 'from-indigo-500 to-indigo-700',
    description: 'Team building and certification requirements',
    type: 'tasks',
    tasks: [
      { id: 'billing_staff',       label: 'Billing/coding specialist hired or contracted' },
      { id: 'delivery_tech',       label: 'Delivery technician(s) hired' },
      { id: 'customer_service',    label: 'Customer service representative hired' },
      { id: 'rt_therapist',        label: 'RT/CRT on staff or contracted (for respiratory products)' },
      { id: 'staff_background',    label: 'Background checks completed for all staff' },
      { id: 'staff_training',      label: 'Product-specific staff training completed' },
      { id: 'cms_training',        label: 'CMS supplier standards training completed' },
      { id: 'employee_handbook',   label: 'Employee handbook created and distributed' },
    ]
  },
  {
    id: 'referrals_marketing',
    label: 'Referrals & Marketing',
    icon: Megaphone,
    color: 'from-pink-500 to-rose-600',
    description: 'Building your referral network and patient pipeline',
    type: 'tasks',
    tasks: [
      { id: 'physician_list',      label: 'Target physician/clinic referral list created' },
      { id: 'physician_outreach',  label: 'Physician outreach and education visits begun' },
      { id: 'hospital_relationships', label: 'Hospital discharge planner relationships established' },
      { id: 'marketing_materials', label: 'Marketing brochures and business cards printed' },
      { id: 'cms1500_training',    label: 'Referral sources educated on proper CMN/documentation' },
      { id: 'patient_pipeline',    label: 'First patient referrals received' },
      { id: 'google_reviews',      label: 'Google review strategy implemented' },
    ]
  },
  {
    id: 'legal_contracts',
    label: 'Legal & Contracts',
    icon: Lock,
    color: 'from-slate-700 to-slate-900',
    description: 'Required agreements, contracts, and legal documents',
    type: 'tasks',
    tasks: [
      { id: 'attorney_retained',     label: 'Healthcare attorney retained' },
      { id: 'privacy_notice',        label: 'HIPAA Privacy Notice drafted and posted' },
      { id: 'patient_agreement',     label: 'Patient service agreement template created' },
      { id: 'abn_template',          label: 'Advance Beneficiary Notice (ABN) template ready' },
      { id: 'supplier_agreement',    label: 'CMS Supplier Standards agreement signed' },
      { id: 'anti_kickback',         label: 'Anti-Kickback Statute training completed' },
      { id: 'records_retention',     label: 'Medical records retention policy in place' },
    ]
  },
];

// ─── COMPONENT ────────────────────────────────────────────────
const DMEProjectPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();

  const [project, setProject] = useState(null);
  const [dmeData, setDmeData] = useState({ fields: {}, tasks: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [expandedTasks, setExpandedTasks] = useState({});
  const [showPasswords, setShowPasswords] = useState({});
  const [dirty, setDirty] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [mediaOpen, setMediaOpen] = useState(false);

  const isReadOnly = user?.role === 'client' || user?.role === 'client_team';
  const isSuperAdmin = user?.role === 'super_admin';
  const isTenant = user?.role === 'admin' || user?.role === 'team';

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    try {
      const [projRes, dmeRes] = await Promise.all([
        axios.get(`${API_URL}/projects/${projectId}`, getAuthHeaders()),
        axios.get(`${API_URL}/projects/${projectId}/dme-data`, getAuthHeaders()).catch(() => ({ data: { fields: {}, tasks: {} } }))
      ]);
      setProject(projRes.data);
      setDmeData(dmeRes.data || { fields: {}, tasks: {} });
      // Only first section open by default
      const expanded = {};
      DME_SECTIONS.forEach((s, i) => { expanded[s.id] = i === 0; });
      setExpandedSections(expanded);
    } catch (err) {
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (fieldId, value) => {
    setDmeData(prev => ({ ...prev, fields: { ...prev.fields, [fieldId]: value } }));
    setDirty(true);
  };

  const toggleTask = (taskId) => {
    if (isReadOnly) return;
    setDmeData(prev => ({
      ...prev,
      tasks: { ...prev.tasks, [taskId]: !prev.tasks[taskId] }
    }));
    setDirty(true);
  };

  const updateTaskDetail = (taskId, fieldKey, value) => {
    setDmeData(prev => ({
      ...prev,
      task_details: {
        ...prev.task_details,
        [taskId]: {
          ...(prev.task_details?.[taskId] || {}),
          [fieldKey]: value
        }
      }
    }));
    setDirty(true);
  };

  const updateTaskCost = (taskId, field, value) => {
    setDmeData(prev => ({
      ...prev,
      task_costs: {
        ...prev.task_costs,
        [taskId]: {
          ...(prev.task_costs?.[taskId] || { amount: '', period: 'mo' }),
          [field]: value
        }
      }
    }));
    setDirty(true);
  };

  const toggleTaskDetail = (taskId) => {
    setExpandedTasks(prev => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const toggleShowPassword = (taskId) => {
    setShowPasswords(prev => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const res = await axios.get(
        `${API_URL}/projects/${projectId}/dme-export/${format}`,
        { ...getAuthHeaders(), responseType: 'blob' }
      );
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const mime = format === 'pdf' ? 'application/pdf'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `DME_Setup_${project?.name?.replace(/\s+/g,'_') || 'project'}.${ext}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch {
      toast.error(`Export failed`);
    } finally {
      setExporting(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/projects/${projectId}/dme-data`, dmeData, getAuthHeaders());
      toast.success('Project saved');
      setDirty(false);
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Calculate overall progress
  const calcProgress = () => {
    let total = 0, completed = 0;
    DME_SECTIONS.forEach(section => {
      if (section.tasks) {
        section.tasks.forEach(task => {
          total++;
          if (dmeData.tasks[task.id]) completed++;
        });
      }
      if (section.fields) {
        section.fields.forEach(f => {
          total++;
          if (dmeData.fields[f.id]?.trim()) completed++;
        });
      }
    });
    return total === 0 ? 0 : Math.round((completed / total) * 100);
  };

  const calcSectionProgress = (section) => {
    let total = 0, completed = 0;
    if (section.tasks) section.tasks.forEach(t => { total++; if (dmeData.tasks[t.id]) completed++; });
    if (section.fields) section.fields.forEach(f => { total++; if (dmeData.fields[f.id]?.trim()) completed++; });
    return { total, completed, pct: total === 0 ? 0 : Math.round((completed / total) * 100) };
  };

  const progress = calcProgress();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="animate-spin text-slate-400" size={36} />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-16 animate-fade-in">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/projects')} className="mt-1 flex-shrink-0">
          <ArrowLeft size={20} />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 bg-gradient-to-br from-red-500 to-rose-700 rounded-xl">
              <Activity size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{project?.name}</h1>
              <p className="text-sm text-slate-500">DME Medical Company Setup Project</p>
            </div>
          </div>
        </div>
        {!isReadOnly && (
          <Button
            onClick={handleSave}
            disabled={saving || !dirty}
            className="bg-slate-900 hover:bg-slate-800 flex-shrink-0"
          >
            {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
            {dirty ? 'Save Changes' : 'Saved'}
          </Button>
        )}
        {/* Export buttons */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleExport('pdf')}
          disabled={exporting === 'pdf'}
          className="flex-shrink-0"
        >
          {exporting === 'pdf' ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Printer size={14} className="mr-1.5" />}
          PDF
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleExport('excel')}
          disabled={exporting === 'excel'}
          className="flex-shrink-0 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
        >
          {exporting === 'excel' ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <FileSpreadsheet size={14} className="mr-1.5" />}
          Excel
        </Button>
        {/* Media Panel Toggle */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setMediaOpen(true)}
          className="flex-shrink-0 text-violet-700 border-violet-300 hover:bg-violet-50"
        >
          <Images size={14} className="mr-1.5" />
          Media
        </Button>
      </div>

      {/* Progress Bar */}
      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm font-semibold text-slate-800">Overall Setup Progress</p>
              <p className="text-xs text-slate-500 mt-0.5">
                {DME_SECTIONS.reduce((a, s) => a + (s.tasks?.length || 0), 0)} tasks ·{' '}
                {DME_SECTIONS.reduce((a, s) => a + (s.fields?.length || 0), 0)} fields
              </p>
            </div>
            <span className={`text-3xl font-bold ${progress >= 75 ? 'text-emerald-600' : progress >= 40 ? 'text-blue-600' : 'text-slate-500'}`}>
              {progress}%
            </span>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all duration-500 ${
                progress >= 75 ? 'bg-gradient-to-r from-emerald-500 to-green-400'
                : progress >= 40 ? 'bg-gradient-to-r from-blue-500 to-cyan-400'
                : 'bg-gradient-to-r from-slate-400 to-slate-500'
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
          {/* Section mini-progress */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mt-4">
            {DME_SECTIONS.map(section => {
              const { pct } = calcSectionProgress(section);
              const SIcon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => {
                    const isAlreadyOpen = expandedSections[section.id];
                    const next = {};
                    DME_SECTIONS.forEach(s => { next[s.id] = false; });
                    next[section.id] = !isAlreadyOpen;
                    setExpandedSections(next);
                  }}
                  className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 text-left transition-colors"
                >
                  <div className={`w-6 h-6 rounded-md bg-gradient-to-br ${section.color} flex items-center justify-center flex-shrink-0`}>
                    <SIcon size={12} className="text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-slate-600 truncate">{section.label}</p>
                    <div className="w-full bg-slate-100 rounded-full h-1 mt-0.5">
                      <div className="h-1 rounded-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Sections */}
      {DME_SECTIONS.map(section => {
        const SIcon = section.icon;
        const { total, completed, pct } = calcSectionProgress(section);
        const isExpanded = !!expandedSections[section.id];

        return (
          <Card key={section.id} className="border-slate-200 shadow-sm overflow-hidden">
            {/* Section Header */}
            <button
              className="w-full text-left"
              onClick={() => {
                const isAlreadyOpen = expandedSections[section.id];
                const next = {};
                DME_SECTIONS.forEach(s => { next[s.id] = false; });
                next[section.id] = !isAlreadyOpen;
                setExpandedSections(next);
              }}
            >
              <div className={`bg-gradient-to-r ${section.color} p-4 text-white`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <SIcon size={20} />
                    </div>
                    <div>
                      <h2 className="font-semibold text-base">{section.label}</h2>
                      <p className="text-xs text-white/80 mt-0.5">{section.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-sm font-bold">{completed}/{total}</p>
                      <p className="text-xs text-white/70">{pct}%</p>
                    </div>
                    {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </div>
                </div>
                {/* Section progress bar */}
                <div className="w-full bg-white/20 rounded-full h-1.5 mt-3">
                  <div className="h-1.5 rounded-full bg-white transition-all duration-500" style={{ width: `${pct}%` }} />
                </div>
              </div>
            </button>

            {/* Section Content */}
            {isExpanded && (
              <CardContent className="p-5 space-y-4">
                {/* Input Fields */}
                {(section.type === 'fields' || section.type === 'mixed') && section.fields?.length > 0 && (
                  <div>
                    {section.type === 'mixed' && (
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">Key Information</p>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {section.fields.map(field => (
                        <div key={field.id} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                          <Label className="text-sm text-slate-700 mb-1.5 block">
                            {field.label}
                            {dmeData.fields[field.id]?.trim() && (
                              <span className="ml-2 inline-flex items-center gap-1 text-xs text-emerald-600 font-normal">
                                <CheckCircle2 size={11} /> Filled
                              </span>
                            )}
                          </Label>
                          {field.type === 'textarea' ? (
                            <Textarea
                              value={dmeData.fields[field.id] || ''}
                              onChange={e => updateField(field.id, e.target.value)}
                              placeholder={field.placeholder}
                              rows={2}
                              disabled={isReadOnly}
                              className="text-sm"
                            />
                          ) : (
                            <Input
                              type={field.type || 'text'}
                              value={dmeData.fields[field.id] || ''}
                              onChange={e => updateField(field.id, e.target.value)}
                              placeholder={field.placeholder}
                              disabled={isReadOnly}
                              className="text-sm"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Checklist Tasks */}
                {(section.type === 'tasks' || section.type === 'mixed') && section.tasks?.length > 0 && (
                  <div>
                    {section.type === 'mixed' && (
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3 mt-2">Checklist</p>
                    )}
                    <div className="space-y-2">
                      {section.tasks.map(task => {
                        const done = !!dmeData.tasks[task.id];
                        const isDetailOpen = !!expandedTasks[task.id];
                        const detail = dmeData.task_details?.[task.id] || {};
                        const hasDetail = Object.values(detail).some(v => v);

                        return (
                          <div key={task.id} className={`rounded-lg border transition-all ${
                            done ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white'
                          }`}>
                            {/* Task row */}
                            <div className="flex items-center gap-2 p-3">
                              <button
                                onClick={() => toggleTask(task.id)}
                                disabled={isReadOnly}
                                className={`flex-shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                                  done
                                    ? 'bg-emerald-500 border-emerald-500 text-white'
                                    : 'border-slate-300 hover:border-emerald-500 hover:bg-emerald-50'
                                } ${isReadOnly ? 'cursor-default' : 'cursor-pointer'}`}
                              >
                                {done && <CheckCircle2 size={13} />}
                              </button>
                              <span className={`flex-1 text-sm ${done ? 'line-through text-emerald-700 opacity-80' : 'text-slate-700'}`}>
                                {task.label}
                              </span>
                              {hasDetail && (
                                <KeyRound size={13} className="text-blue-400 flex-shrink-0" title="Has credentials" />
                              )}

                              {/* Cost field — super admin edits, tenant reads (if filled), client sees nothing */}
                              {isSuperAdmin && (
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <span className="text-xs text-slate-400">$</span>
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={dmeData.task_costs?.[task.id]?.amount || ''}
                                    onChange={e => updateTaskCost(task.id, 'amount', e.target.value)}
                                    placeholder="0.00"
                                    className="w-16 h-6 text-xs border border-slate-200 rounded px-1 text-right focus:outline-none focus:border-blue-400 bg-white"
                                    onClick={e => e.stopPropagation()}
                                  />
                                  <select
                                    value={dmeData.task_costs?.[task.id]?.period || 'mo'}
                                    onChange={e => updateTaskCost(task.id, 'period', e.target.value)}
                                    onClick={e => e.stopPropagation()}
                                    className="h-6 text-xs border border-slate-200 rounded px-1 focus:outline-none focus:border-blue-400 bg-white text-slate-600 cursor-pointer"
                                  >
                                    <option value="once">1 time</option>
                                    <option value="mo">/mo</option>
                                    <option value="yr">/yr</option>
                                  </select>
                                </div>
                              )}
                              {(isTenant) && (() => {
                                const cost = dmeData.task_costs?.[task.id];
                                if (!cost?.amount) return null;
                                return (
                                  <span className="text-xs font-medium text-slate-500 flex-shrink-0 bg-slate-100 px-2 py-0.5 rounded">
                                    ${parseFloat(cost.amount).toFixed(2)}{cost.period === 'once' ? ' (1×)' : cost.period === 'mo' ? '/mo' : '/yr'}
                                  </span>
                                );
                              })()}

                              {!isReadOnly && (
                                <button
                                  onClick={() => toggleTaskDetail(task.id)}
                                  className={`flex-shrink-0 flex items-center gap-1 text-xs px-2 py-1 rounded-md transition-colors ${
                                    isDetailOpen
                                      ? 'bg-blue-100 text-blue-700'
                                      : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
                                  }`}
                                  title="Add credentials / details"
                                >
                                  {isDetailOpen ? <ChevronDown size={12} /> : <Plus size={12} />}
                                  {isDetailOpen ? 'Close' : 'Details'}
                                </button>
                              )}
                            </div>

                            {/* Credential detail panel */}
                            {isDetailOpen && (
                              <div className="px-4 pb-4 pt-1 border-t border-slate-100">
                                <p className="text-xs text-slate-400 mb-3 flex items-center gap-1">
                                  <Lock size={11} />
                                  Credential details — AES-256 encrypted · Passwords hidden in exports
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  {/* Service / Registrar */}
                                  <div>
                                    <Label className="text-xs text-slate-500 mb-1 block">Service / Registrar Name</Label>
                                    <Input
                                      value={detail.service_name || ''}
                                      onChange={e => updateTaskDetail(task.id, 'service_name', e.target.value)}
                                      placeholder="e.g. GoDaddy, Namecheap"
                                      className="h-8 text-sm"
                                    />
                                  </div>
                                  {/* URL */}
                                  <div>
                                    <Label className="text-xs text-slate-500 mb-1 block">Web URL</Label>
                                    <Input
                                      value={detail.url || ''}
                                      onChange={e => updateTaskDetail(task.id, 'url', e.target.value)}
                                      placeholder="https://..."
                                      className="h-8 text-sm"
                                    />
                                  </div>
                                  {/* Username */}
                                  <div>
                                    <Label className="text-xs text-slate-500 mb-1 block">Username / Login</Label>
                                    <Input
                                      value={detail.username || ''}
                                      onChange={e => updateTaskDetail(task.id, 'username', e.target.value)}
                                      placeholder="username or email"
                                      className="h-8 text-sm"
                                    />
                                  </div>
                                  {/* Password */}
                                  <div>
                                    <Label className="text-xs text-slate-500 mb-1 block">Password</Label>
                                    <div className="relative">
                                      <Input
                                        type={showPasswords[task.id] ? 'text' : 'password'}
                                        value={detail.password || ''}
                                        onChange={e => updateTaskDetail(task.id, 'password', e.target.value)}
                                        placeholder="••••••••"
                                        className="h-8 text-sm pr-8"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => toggleShowPassword(task.id)}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                      >
                                        {showPasswords[task.id] ? <EyeOff size={13} /> : <Eye size={13} />}
                                      </button>
                                    </div>
                                  </div>
                                  {/* Account Number */}
                                  <div>
                                    <Label className="text-xs text-slate-500 mb-1 block">Account Number</Label>
                                    <Input
                                      value={detail.account_number || ''}
                                      onChange={e => updateTaskDetail(task.id, 'account_number', e.target.value)}
                                      placeholder="Account or member #"
                                      className="h-8 text-sm"
                                    />
                                  </div>
                                  {/* Email */}
                                  <div>
                                    <Label className="text-xs text-slate-500 mb-1 block">Email on Account</Label>
                                    <Input
                                      type="email"
                                      value={detail.email || ''}
                                      onChange={e => updateTaskDetail(task.id, 'email', e.target.value)}
                                      placeholder="email@example.com"
                                      className="h-8 text-sm"
                                    />
                                  </div>
                                  {/* Notes */}
                                  <div className="sm:col-span-2">
                                    <Label className="text-xs text-slate-500 mb-1 block">Notes</Label>
                                    <Input
                                      value={detail.notes || ''}
                                      onChange={e => updateTaskDetail(task.id, 'notes', e.target.value)}
                                      placeholder="Renewal date, pin, security question..."
                                      className="h-8 text-sm"
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        );
      })}

      {/* Floating Save */}
      {!isReadOnly && dirty && (
        <div className="fixed bottom-6 right-20 z-30">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-900/20 flex items-center gap-2"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Save Changes
          </Button>
        </div>
      )}

      {/* DME Media Panel - only mounted when open */}
      {mediaOpen && (
        <DMEMediaPanel
          projectId={projectId}
          onClose={() => setMediaOpen(false)}
          getAuthHeaders={getAuthHeaders}
        />
      )}
    </div>
  );
};

export default DMEProjectPage;
